import React from "react"
import { Box, Paper, Stack, Typography } from "@mui/material"
import SideBarLayout from "../../../layout/side-bar-layout"
import { TITLE_DESIGN_SYSTEM_PAGE } from "../../../../utils/constants"
import CodeViewer from "../../../components/CodeViewer/code-viewer"
import InfoDataPanel from "src/v2/components/InfoDataPanel/info-data-panel"

const InfoDataPanelDesignSystemPage = () => {
    const TITLE_PAGE = `${TITLE_DESIGN_SYSTEM_PAGE} | Info Data Panel`

    const exampleData = {
        CETIP: "ABC123",
        ISIN: "BR0000000000",
        Serie: "1",
        Emissao: "2",
        DataEmissao: "2026-01-10",
        DataVencimento: "2030-01-10",
        PU_Emissao: "1000",
        QuantidadeEmitida: "50000",
        Devedor: "Empresa Exemplo S.A.",
        CPF_CNPJ_Devedor: "00000000000100",
        GrupoEconomico: "Grupo Exemplo",
        CNPJGrupoEconomico: "00000000",
        Setor: "Financeiro",
        SubSetor: "Bancos",
        DestinacaoRecursos: "Capital de giro e investimentos operacionais.",
    }

    const leftItems = [
        {
            key: "CETIP",
            label: "Cetip",
            gridProps: { xxxs: 2, xs: 2, md: 12, lg: 12 },
        },
        {
            key: "ISIN",
            label: "ISIN",
            gridProps: { xxxs: 2, xs: 2, md: 12, lg: 12 },
        },
        {
            key: "Serie",
            label: "Série",
            gridProps: { xxxs: 2, xs: 2, md: 12, lg: 6 },
        },
        {
            key: "Emissao",
            label: "Emissão",
            gridProps: { xxxs: 2, xs: 2, md: 12, lg: 6 },
        },
    ]

    const sections = [
        {
            items: [
                {
                    key: "DataEmissao",
                    label: "Data Emissão",
                },
                {
                    key: "DataVencimento",
                    label: "Data Vencimento",
                },
                {
                    key: "PU_Emissao",
                    label: "PU Emissão",
                },
                {
                    key: "QuantidadeEmitida",
                    label: "Quantidade Emitida",
                },
            ],
        },
        {
            variant: "divided",
            items: [
                {
                    key: "Devedor",
                    label: "Devedor",
                    optionalTextFormatter: (data) => data?.CPF_CNPJ_Devedor,
                    optionalTextOpacity: 0.6,
                    optionalTextVariant: "captionInfo",
                },
                {
                    key: "GrupoEconomico",
                    label: "Grupo Econômico",
                    optionalTextFormatter: (data) =>
                        data?.CNPJGrupoEconomico,
                    optionalTextOpacity: 0.6,
                    optionalTextVariant: "captionInfo",
                },
                {
                    key: "Setor",
                    label: "Setor",
                    optionalTextLabel: "Subsetor",
                    optionalTextFormatter: (data) => data?.SubSetor,
                },
                {
                    key: "DestinacaoRecursos",
                    label: "Destinação",
                },
            ],
        },
    ]

    const bottomItems = [
        {
            key: "DestinacaoRecursos",
            label: "Destinação de Recursos",
        },
    ]

    const codeUsage = `import InfoDataPanel from "src/v2/components/InfoDataPanel/info-data-panel"

const data = {
    CETIP: "ABC123",
    ISIN: "BR0000000000",
    Serie: "1",
    Emissao: "2",
    DataEmissao: "2026-01-10",
    DataVencimento: "2030-01-10",
    Devedor: "Empresa Exemplo S.A.",
    CPF_CNPJ_Devedor: "00000000000100",
    Setor: "Financeiro",
    SubSetor: "Bancos",
    DestinacaoRecursos: "Capital de giro e investimentos operacionais.",
}

const leftItems = [
    {
        key: "CETIP",
        label: "Cetip",
        gridProps: { xxxs: 2, xs: 2, md: 12, lg: 12 },
    },
    {
        key: "ISIN",
        label: "ISIN",
        gridProps: { xxxs: 2, xs: 2, md: 12, lg: 12 },
    },
]

const sections = [
    {
        items: [
            {
                key: "DataEmissao",
                label: "Data Emissão",
            },
            {
                key: "DataVencimento",
                label: "Data Vencimento",
            },
        ],
    },
    {
        variant: "divided",
        items: [
            {
                key: "Devedor",
                label: "Devedor",
                optionalTextFormatter: (data) => data?.CPF_CNPJ_Devedor,
                optionalTextOpacity: 0.6,
                optionalTextVariant: "captionInfo",
            },
            {
                key: "Setor",
                label: "Setor",
                optionalTextLabel: "Subsetor",
                optionalTextFormatter: (data) => data?.SubSetor,
            },
        ],
    },
]

const bottomItems = [
    {
        key: "DestinacaoRecursos",
        label: "Destinação de Recursos",
    },
]

<InfoDataPanel
    title="Dados da emissão"
    data={data}
    leftItems={leftItems}
    sections={sections}
    bottomItems={bottomItems}
/>`

    const codeProps = `type InfoDataPanelProps = {
  title?: string,          // Título opcional do painel
  data?: object,           // Objeto com os dados usados pelos itens
  leftItems?: InfoItemConfig[], // Itens exibidos na área lateral
  sections?: SectionConfig[],   // Seções principais do painel
  bottomItems?: InfoItemConfig[] // Itens exibidos abaixo do painel
}

type SectionConfig = {
  variant?: "grid" | "divided",
  sx?: object,
  items: InfoItemConfig[]
}

type InfoItemConfig = {
  key?: string,
  label?: string,
  formatter?: (value, data) => ReactNode,
  optionalText?: ReactNode,
  optionalTextFormatter?: (data) => ReactNode,
  optionalTextLabel?: string,
  optionalTextOpacity?: number,
  optionalTextVariant?: string,
  gridProps?: object,
  sx?: object,
  placeholder?: boolean
}`

    return (
        <SideBarLayout header title={TITLE_PAGE} headerTitle={TITLE_PAGE}>
            <Box sx={{ mt: -2 }}>
                <Stack spacing={3}>
                    <Paper>
                        <Box p={3}>
                            <Typography
                                variant="h4"
                                gutterBottom
                                color="common.white"
                            >
                                InfoDataPanel
                            </Typography>

                            <Typography variant="body2" color="text.secondary">
                                Componente utilizado para montar painéis de
                                dados a partir de configurações. Permite
                                organizar campos laterais, seções em grid,
                                seções divididas e campos inferiores, mantendo
                                responsividade e reaproveitamento visual.
                            </Typography>

                            <Box mt={4}>
                                <InfoDataPanel
                                    title="Dados da emissão"
                                    data={exampleData}
                                    leftItems={leftItems}
                                    sections={sections}
                                    bottomItems={bottomItems}
                                />
                            </Box>
                        </Box>
                    </Paper>

                    <Paper>
                        <Box p={3}>
                            <Typography
                                variant="h6"
                                gutterBottom
                                color="common.white"
                            >
                                Uso básico
                            </Typography>

                            <Typography
                                variant="body2"
                                color="text.secondary"
                                paragraph
                            >
                                Utilize o componente quando precisar montar uma
                                área de informações com campos configuráveis,
                                evitando duplicação de estruturas com Box, Grid
                                e InfoItem.
                            </Typography>

                            <Paper variant="outlined">
                                <Box p={2}>
                                    <CodeViewer
                                        code={codeUsage}
                                        language="jsx"
                                    />
                                </Box>
                            </Paper>
                        </Box>
                    </Paper>

                    <Paper>
                        <Box p={3}>
                            <Typography
                                variant="h6"
                                gutterBottom
                                color="common.white"
                            >
                                Propriedades principais
                            </Typography>

                            <Typography
                                variant="body2"
                                color="text.secondary"
                                paragraph
                            >
                                Interface do componente e das configurações de
                                itens/seções.
                            </Typography>

                            <Paper variant="outlined">
                                <Box p={2}>
                                    <CodeViewer
                                        code={codeProps}
                                        language="tsx"
                                    />
                                </Box>
                            </Paper>
                        </Box>
                    </Paper>
                </Stack>
            </Box>
        </SideBarLayout>
    )
}

export default InfoDataPanelDesignSystemPage